<script lang="ts">
  import {
    CLASSES,
    TALENT_SLOTS,
    abilityDef,
    auraDef,
    type AbilityId,
    type AuraId,
    type TalentSlot,
  } from '@arena/shared';
  import { click } from '../game/Sfx';
  import { app } from '../lib/net.svelte';
  import { pickTalent, talentsFor } from '../lib/settings.svelte';

  let {
    locked,
    onclose,
  }: {
    /** In a match (but not practice) the talents are set. */
    locked: boolean;
    onclose: () => void;
  } = $props();

  const SLOTS: Record<TalentSlot, { name: string; rule: string }> = {
    arrival: { name: 'Arrival', rule: 'As you swap into this class' },
    departure: { name: 'Departure', rule: 'As you swap out of it' },
    discipline: { name: 'Discipline', rule: 'Always on' },
  };

  const talent = (slot: TalentSlot, id: string) => {
    const def =
      slot === 'discipline'
        ? auraDef(id as AuraId)
        : abilityDef(id as AbilityId);
    return {
      name: def.name,
      description: def.description,
      icon: def.icon ?? id,
    };
  };

  function onKey(event: KeyboardEvent) {
    if (event.code !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    click();
    onclose();
  }
</script>

<svelte:window onkeydowncapture={onKey} />

<section class="panel talents">
  <header>
    <h2>Talents</h2>
    {#each app.classes as id (id)}<span style="color: {CLASSES[id].color}"
        >{CLASSES[id].name}</span
      >{/each}
    <button class="close" onclick={onclose} aria-label="Close">×</button>
  </header>

  <div class="content">
    <p class="muted small">
      {locked
        ? 'Your talents are set for this match.'
        : 'Pick one talent in each slot for both your classes. Swapping fires the departure of the class you leave, then the arrival of the one you take up. Opponents never see your talents.'}
    </p>
    {#each app.classes as classId (classId)}
      {@const picked = talentsFor(classId)}
      <h3 style:color={CLASSES[classId].color}>{CLASSES[classId].name}</h3>
      <div class="slots">
        {#each TALENT_SLOTS as slot, i (slot)}
          <div
            class="slot"
            role="radiogroup"
            aria-label="{CLASSES[classId].name} {SLOTS[slot].name}"
          >
            <h4>
              {SLOTS[slot].name} <span class="muted">{SLOTS[slot].rule}</span>
            </h4>
            {#each CLASSES[classId].talents[slot] as id (id)}
              {@const info = talent(slot, id)}
              <button
                class="talent"
                class:chosen={picked[i] === id}
                role="radio"
                aria-checked={picked[i] === id}
                disabled={locked}
                onclick={() => pickTalent(classId, i, id)}
              >
                <img src="/icons/{info.icon}.webp" alt="" />
                <span class="text">
                  <strong>{info.name}</strong>
                  <span class="small">{info.description}</span>
                </span>
              </button>
            {/each}
          </div>
        {/each}
      </div>
    {/each}
  </div>
</section>

<style>
  .talents {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    width: min(820px, calc(100% - 24px));
    max-height: min(680px, calc(100% - 200px));
    padding: 0;
    pointer-events: auto;
    background: var(--panel-solid);
    border-color: var(--gold);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
    font-size: 14px;
  }
  header {
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 10px 18px;
    border-bottom: 1px solid var(--border);
  }
  header h2 {
    margin: 0;
  }
  .close {
    margin-left: auto;
    padding: 0 8px;
    font-size: 18px;
  }
  .content {
    overflow-y: auto;
    padding: 6px 18px 14px;
  }
  h3 {
    margin: 14px 0 6px;
    font-size: 15px;
  }
  h4 {
    margin: 0 0 6px;
    font-size: 13px;
  }
  .slots {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }
  .slot {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  /* Room for a name and three lines of description, so every card in a slot lines up. */
  .talent {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    height: 80px;
    overflow: hidden;
    padding: 6px 8px 6px 6px;
    text-align: left;
    border: 1px solid var(--border);
    border-radius: 4px;
    background: #13151c;
  }
  .talent.chosen {
    border-color: var(--gold);
    background: rgba(230, 192, 106, 0.1);
  }
  .talent:disabled:not(.chosen) {
    opacity: 0.45;
  }
  .talent:disabled {
    cursor: default;
  }
  img {
    flex: none;
    width: 36px;
    height: 36px;
    border: 2px solid #6b5a35;
    border-radius: 4px;
  }
  .chosen img {
    border-color: var(--gold);
  }
  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .muted {
    color: var(--muted);
    font-weight: normal;
  }
  .small {
    font-size: 12px;
    line-height: 1.25;
    color: var(--muted);
  }
</style>
